import { ChangeDetectionStrategy, Component, Input, OnInit, signal, WritableSignal } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Bind } from "../../decorators/bind.decorator";
import { Item } from "../../pojo/Item";
import { S25Util } from "../../util/s25-util";
import { ContactService } from "../../services/contact.service";
import { OrganizationService } from "../../services/organization.service";
import { SpaceService } from "../../services/space.service";
import { ResourceMicroService } from "../../services/resource.micro.service";
import { EventMicroService } from "../../services/event.micro.service";
import { EventProfileMicroService } from "../../services/event.profile.micro.service";

@TypeManagerDecorator("s25-ng-object-details-editable-text")
@Component({
    selector: "s25-ng-object-details-editable-text",
    template: `
        @if (!data && !canEdit && !profiles?.length) {
            none
        } @else {
            @switch (type) {
                @case ("richtext") {
                    <s25-ng-editable-richtext
                        [(val)]="data"
                        [readOnly]="!canEdit || !canEditComments"
                        [autoResize]="fadePreview()"
                        [fadePreview]="fadePreview()"
                        [hasCommit]="true"
                        [onCommit]="save"
                    ></s25-ng-editable-richtext>
                }
                @case ("textarea") {
                    <s25-ng-editable-textarea
                        [(val)]="data"
                        [readOnly]="!canEdit"
                        [fadePreview]="fadePreview()"
                        [hasCommit]="true"
                        [onCommit]="save"
                    ></s25-ng-editable-textarea>
                }
                @case ("multi-textarea") {
                    @for (profile of profiles; track profile.profileId) {
                        <div [class.multi-profiles]="profiles?.length > 1">
                            @if (profiles.length > 1) {
                                <label>{{ profile.name }}</label>
                            }
                            <s25-ng-editable-textarea
                                [(val)]="profile.comments"
                                [readOnly]="!canEdit"
                                [fadePreview]="profile.fadePreview()"
                                [hasCommit]="true"
                                [onCommit]="save"
                                (valChange)="eventProfileSaveData = profile"
                            ></s25-ng-editable-textarea>
                        </div>
                    }
                }
            }
        }
    `,
    host: {
        "[class.fade-preview-container]": "fadePreview()",
        "(click)": "toggleFadePreview()",
    },
    styles: `
        :host {
            display: block;
            padding: 1rem;

            .multi-profiles {
                display: grid;
                grid-template-columns: 28% 72%;

                label {
                    font-weight: 400 !important;
                    grid-column: 1 / 2;
                }

                s25-ng-editable-textarea {
                    grid-column: 2 / 3;
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25ObjectDetailsEditableTextComponent implements OnInit {
    @Input({ required: true }) itemId: number;
    @Input({ required: true }) itemTypeId: number;
    @Input() data: string; // single string item
    @Input() profiles: ProfileComments[] = [];
    @Input({ required: true }) canEdit: boolean;
    @Input({ required: true }) type: "textarea" | "richtext" | "multi-textarea";
    @Input({ required: true }) prop: "profiles" | "description" | "comments" | "instructions" | "note" | "confirmation";
    @Input() canEditComments?: boolean = true;

    fadePreview = signal(false);
    eventProfileSaveData: ProfileComments;

    ngOnInit() {
        if (this.prop === "profiles") this.profiles ??= [];
        this.setFadePreview();
    }

    @Bind
    async save() {
        const payload = { [this.prop]: this.data };
        let updateCommentsPromise;
        switch (this.itemTypeId) {
            case Item.Ids.Contact:
                updateCommentsPromise = ContactService.putContactMinimal(this.itemId, payload);
                break;
            case Item.Ids.Organization:
                updateCommentsPromise = OrganizationService.putOrganization(this.itemId, payload);
                break;
            case Item.Ids.Location:
                updateCommentsPromise = SpaceService.microUpdate({ id: this.itemId, ...payload });
                break;
            case Item.Ids.Resource:
                updateCommentsPromise = ResourceMicroService.saveResource({ id: this.itemId, ...payload });
                break;
            case Item.Ids.Event:
                updateCommentsPromise = this.saveEventText();
        }
        const [_, error] = await S25Util.Maybe(updateCommentsPromise);
        if (error) return S25Util.showError(error);
        this.setFadePreview();
    }

    setFadePreview() {
        this.fadePreview.set(
            this.data?.length > 350 || this.profiles?.some((profile) => profile.comments?.length > 350),
        );
    }

    async saveEventText() {
        if (this.prop === "profiles") return this.saveProfileText();
        const payload = {
            items: [
                {
                    id: this.itemId,
                    text: [
                        {
                            [this.prop]: this.data,
                        },
                    ],
                },
            ],
        };
        const [_, error] = await S25Util.Maybe(EventMicroService.microPutEventDetail(payload, this.itemId));
        if (error) return console.error(error);
    }

    async saveProfileText() {
        const { profileId, comments } = this.eventProfileSaveData;
        this.eventProfileSaveData?.fadePreview.set(comments.length > 350);
        const [_, error] = await S25Util.Maybe(
            EventProfileMicroService.updateProfileMinimal(this.itemId, profileId, { comments }),
        );
        if (error) return console.error(error);
        this.eventProfileSaveData = null;
    }

    toggleFadePreview() {
        if (!this.canEdit) this.fadePreview.set(!this.fadePreview());
    }
}

export type ProfileComments = {
    name: string;
    profileId: number;
    comments: string;
    fadePreview: WritableSignal<boolean>;
};
